/**
 * 🌧️ Rain — the second thing through the particle machinery, and the reason it
 * was built the way it was.
 *
 * **T128's smoke made this · D64 §4 · D85**
 *
 * 🎯 **haugur, 2026-09-01:** *"we have weather data in the game, it tells us if
 * its raining or not, im not sure how you 'draw' the rain element."*
 *
 * ## What a raindrop actually is
 *
 * **A tiny flat picture that always turns to face you** — an upright streak on a
 * card, not a 3D object. A few thousand of them live in an **invisible box** and
 * fall; one that drops out of the bottom reappears at the top. **The box travels
 * with you**, so you never reach its edge and never run out of rain.
 *
 * ## 🔴 Nothing is moved one drop at a time
 *
 * Each drop knows **where it started and how fast it falls**, and the shader
 * works out where it is *now* from the clock. **The CPU writes one number per
 * frame — the time — for all four thousand of them.**
 *
 * ⚠️ **This is the whole reason the smoke was written this way first.** Moving a
 * few thousand positions on the processor sixty times a second is exactly the
 * mistake that cost the frame rate on 2026-08-31, and rain is where that mistake
 * would actually have hurt.
 *
 * ## 🔴 The drops stay still and YOU move through them
 *
 * ⚠️ **The obvious shortcut is to park the box on the camera**, which is what
 * Minecraft does — and then the rain slides along with you as you walk, because
 * every drop is at a fixed offset from your eye.
 *
 * ✅ **Instead each drop belongs to a LATTICE fixed in the world**, and is drawn
 * at whichever copy of that lattice is nearest the camera. Walk, and you pass
 * through them. It costs one `round()` per axis and it is the difference between
 * rain and a screen effect.
 *
 * ## Where it does not fall
 *
 * 🔴 **`shelter.ts` already answers *are you under a roof*, and this asks it
 * rather than working it out again.** The handoff was explicit: *"do not write a
 * second answer to that question."* The lee of a wall still gets rained on; a
 * roof does not.
 */

import {
  BufferAttribute,
  BufferGeometry,
  DataTexture,
  DoubleSide,
  LinearFilter,
  Mesh,
  NormalBlending,
  RGBAFormat,
} from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import {
  attribute,
  billboarding,
  cameraPosition,
  float,
  texture,
  uniform,
  uv,
  vec3,
} from 'three/tsl';

/**
 * How wide and how tall the invisible box of rain is, in metres.
 *
 * ⚠️ **Wide enough that the far edge is past the fog and the near edge is behind
 * you.** 🔴 **Its size is also the lattice spacing**, so making it bigger does not
 * add drops — it spreads the same drops thinner. **Density is {@link DROPS}.**
 */
export const BOX_WIDE = 34;
export const BOX_TALL = 26;

/**
 * How many drops are in the air at once.
 *
 * ⚠️ **A PLAYTEST number**, and the one to reach for first if rain looks thin or
 * looks like a curtain. Four thousand cards is nothing to draw and everything to
 * the look.
 */
export const DROPS = 4000;

/** How fast a drop falls, in metres per second — slowest to fastest. */
const SPEED = [22, 30] as const;

/** How long and how wide one streak is, in metres. */
const STREAK_TALL = [0.34, 0.62] as const;
const STREAK_WIDE = 0.022;

/**
 * ❄️ **Snow is the same rain, slowed down and fattened up.**
 *
 * 🔴 **`weather.ts` already knows which is falling** — `fallingAsSnow`, decided
 * by temperature — so nothing here has to guess. ⚠️ **Snow is NOT built yet**:
 * these are the two numbers it will want, kept beside the rain ones so the day
 * somebody builds it the shape is obvious rather than rediscovered.
 */
export const SNOW_SPEED = [1.4, 2.6] as const;

/**
 * 🌧️ **A streak, drawn rather than photographed.**
 *
 * ⚠️ **Procedural, so it costs nothing and needs no sign-off** — the same
 * argument as the smoke puff. A raindrop is a soft bright line; asking an image
 * model for one would be spending money on a gradient.
 *
 * 🔴 **Fades at BOTH ends.** A streak with hard ends reads as a falling stick.
 */
export function buildRainTexture(w = 8, h = 32): DataTexture {
  const data = new Uint8Array(w * h * 4);
  for (let y = 0; y < h; y++) {
    /* 0 at both ends, 1 in the middle — the streak's own taper. */
    const along = Math.sin((y / (h - 1)) * Math.PI);
    for (let x = 0; x < w; x++) {
      /* Brightest down the centre line, gone at the edges. */
      const across = 1 - Math.abs((x - (w - 1) / 2) / ((w - 1) / 2));
      const i = (y * w + x) * 4;
      /* eslint-disable security/detect-object-injection --
         Loop counters into an array this function just allocated. Same reasoning
         and same wording as `textures.ts`, `flame.ts` and `smoke.ts`. */
      data[i] = 208;
      data[i + 1] = 220;
      data[i + 2] = 232;
      data[i + 3] = Math.round(Math.max(0, along * across) * 255);
      /* eslint-enable security/detect-object-injection */
    }
  }
  const tex = new DataTexture(data, w, h, RGBAFormat);
  tex.magFilter = LinearFilter;
  tex.minFilter = LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/** Seconds, set once per frame. Every drop's fall is a function of this. */
export const rainTime = uniform(0);

/**
 * How hard it is raining, 0–1 — **and 0 the moment you step under a roof.**
 *
 * 🔴 **The caller multiplies the weather by the shelter**, because both answers
 * already exist and neither belongs in here.
 */
export const rainStrength = uniform(0);

/**
 * The material every drop shares.
 *
 * 🔴 **`billboarding` with its default — horizontal only.** A drop turns to face
 * you as you walk round it and **stays upright**, which is exactly what rain
 * does. ⚠️ Turning vertically as well would tip the streaks over when you look
 * up, and rain that leans with the camera reads as a bug.
 *
 * ⚠️ **`.round()`, `.mul()`, `.add()`, `.sub()`, `.div()` only** — the small
 * vocabulary `flame.ts` and `smoke.ts` have already proved on this renderer.
 * **After 2026-09-01 that restraint is the policy, not a preference.**
 */
export function buildRainMaterial(tex: DataTexture): MeshBasicNodeMaterial {
  const material = new MeshBasicNodeMaterial();

  const offset = attribute('offset', 'vec3');
  const fallen = rainTime.mul(attribute('speed', 'float'));

  /**
   * 🔴 **Whichever copy of the lattice is nearest the camera.**
   *
   * `v + s·round((c − v)/s)` puts a point that repeats every `s` at the repeat
   * closest to `c`. ⚠️ **It is what makes the drops stand still in the world
   * while the box follows you** — the alternative is rain glued to your eye.
   */
  const nearest = (v: ReturnType<typeof float>, c: ReturnType<typeof float>, s: number) =>
    v.add(float(s).mul(c.sub(v).div(float(s)).round()));

  const centre = vec3(
    nearest(offset.x, cameraPosition.x, BOX_WIDE),
    nearest(offset.y.sub(fallen), cameraPosition.y, BOX_TALL),
    nearest(offset.z, cameraPosition.z, BOX_WIDE),
  );

  material.vertexNode = billboarding({ position: centre });

  const tile = texture(tex, uv());
  material.colorNode = vec3(tile.r, tile.g, tile.b);
  material.opacityNode = tile.a.mul(rainStrength).mul(attribute('alpha', 'float'));

  material.transparent = true;
  /* ⚠️ Thousands of overlapping translucent cards. Writing depth would make each
     cut a hole in the ones behind it — the same reason the flame and the smoke
     do not. They still depth-TEST, so rain goes behind a wall. */
  material.depthWrite = false;
  material.blending = NormalBlending;
  material.side = DoubleSide;
  /* Anything this faint is not a raindrop, and dropping it early matters here
     more than anywhere else: four thousand cards is a lot of fill. */
  material.alphaTest = 0.02;

  return material;
}

/* A tiny deterministic hash. ⚠️ NOT `Math.random`: the rain is built once and
   must be the same every time, or a reload would shuffle every drop. The same
   rule `berries.ts` and `smoke.ts` already follow. */
function hash(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/* eslint-disable security/detect-object-injection --
   Numeric work over typed arrays: every index is a loop counter bounded above,
   writing into an array this function just allocated. Same reasoning and same
   wording as `flame.ts`, `smoke.ts` and `textures.ts`. */

/**
 * 🌧️ **Every drop, as one geometry.**
 *
 * @param drops how many. ⚠️ **Built ONCE for the life of the session** — rain
 *   starting and stopping is {@link rainStrength}, not a rebuild.
 */
export function buildRainGeometry(drops = DROPS): BufferGeometry | null {
  if (drops <= 0) return null;

  const verts = drops * 4;
  const pos = new Float32Array(verts * 3);
  const uvs = new Float32Array(verts * 2);
  const offset = new Float32Array(verts * 3);
  const speed = new Float32Array(verts);
  const alpha = new Float32Array(verts);
  const indices = new Uint32Array(drops * 6);

  const CORNERS: readonly (readonly [number, number, number, number])[] = [
    [-0.5, -0.5, 0, 1],
    [0.5, -0.5, 1, 1],
    [0.5, 0.5, 1, 0],
    [-0.5, 0.5, 0, 0],
  ];

  for (let d = 0; d < drops; d++) {
    const seed = d * 7919;
    const ox = hash(seed) * BOX_WIDE;
    const oy = hash(seed + 1) * BOX_TALL;
    const oz = hash(seed + 2) * BOX_WIDE;
    const fall = SPEED[0] + (SPEED[1] - SPEED[0]) * hash(seed + 3);
    /* ⚠️ Longer streaks fall faster, which is what a camera sees and what makes
       a mixed bag read as rain rather than as confetti. */
    const tall = STREAK_TALL[0] + (STREAK_TALL[1] - STREAK_TALL[0]) * hash(seed + 4);
    /* Not every drop is equally close, so not every drop is equally solid. */
    const solid = 0.45 + hash(seed + 5) * 0.55;
    const base = d * 4;

    for (const [c, corner] of CORNERS.entries()) {
      const v = base + c;
      pos[v * 3] = corner[0] * STREAK_WIDE;
      pos[v * 3 + 1] = corner[1] * tall;
      pos[v * 3 + 2] = 0;
      uvs[v * 2] = corner[2];
      uvs[v * 2 + 1] = corner[3];
      offset[v * 3] = ox;
      offset[v * 3 + 1] = oy;
      offset[v * 3 + 2] = oz;
      speed[v] = fall;
      alpha[v] = solid;
    }

    const q = d * 6;
    indices[q] = base;
    indices[q + 1] = base + 1;
    indices[q + 2] = base + 2;
    indices[q + 3] = base;
    indices[q + 4] = base + 2;
    indices[q + 5] = base + 3;
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(pos, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setAttribute('offset', new BufferAttribute(offset, 3));
  geometry.setAttribute('speed', new BufferAttribute(speed, 1));
  geometry.setAttribute('alpha', new BufferAttribute(alpha, 1));
  geometry.setIndex(new BufferAttribute(indices, 1));
  return geometry;
}
/* eslint-enable security/detect-object-injection */

/**
 * 🌧️ **The rain, as one object added to the scene once and never rebuilt.**
 *
 * 🔴 **It is ALWAYS in the scene, even in July.** That is deliberate and it is
 * the lesson of 2026-09-01 built in: **a material's shader is not compiled until
 * something using it is drawn**, so rain that only appeared in a downpour would
 * be a shader nobody had ever run — and it would fail the first time the weather
 * turned, in front of haugur, exactly as the smoke did.
 *
 * ✅ **`rainStrength` is 0 when it is dry**, so every drop is discarded by
 * `alphaTest` before it shades a pixel. **It costs a vertex pass and buys a
 * shader that is proven on every boot.**
 */
export function buildRainMesh(material: MeshBasicNodeMaterial, drops = DROPS): Mesh | null {
  const geometry = buildRainGeometry(drops);
  if (geometry === null) return null;
  const mesh = new Mesh(geometry, material);
  /* After the world, the flames and the smoke. */
  mesh.renderOrder = 3;
  /* ⚠️ The shader moves every vertex to wherever the camera is, so a bounding
     box computed from the geometry means nothing and would cull the lot. */
  mesh.frustumCulled = false;
  return mesh;
}
